import { ConversationStoreBindingCard } from "@/features/merchant-home/ConversationStoreBindingCard";
import type { MerchantHomeBindingPart } from "@/features/merchant-home/merchantHomeBindingSchema";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button, Menu, MenuItem, MenuTrigger, Popover } from "react-aria-components";
import { useLocation, useNavigate } from "react-router";

import arrowDownIcon from "@/assets/figma/chat-workflow/arrow-down.svg";
import arrowRightIcon from "@/assets/figma/chat-workflow/arrow-right.svg";
import chatAiIcon from "@/assets/figma/chat-workflow/chat-ai.svg";
import checkIcon from "@/assets/figma/chat-workflow/check.svg";
import copyIcon from "@/assets/figma/chat-workflow/copy.svg";
import thinkIcon from "@/assets/figma/chat-workflow/think.svg";
import retrievalIcon from "@/assets/figma/chat-workflow/retrieval.svg";
import toolIcon from "@/assets/figma/chat-workflow/tool.svg";
import approvalIcon from "@/assets/figma/chat-workflow/approval.svg";
import loadIcon from "@/assets/figma/chat-workflow/load.svg";
import moreIcon from "@/assets/figma/chat-workflow/more.svg";
import quoteIcon from "@/assets/figma/chat-context-bar/quote.svg";
import {
  MerryMarkdown,
  MerryMarkdownDocumentView,
  useMerryMarkdownDocument,
} from "@/features/chat/markdown";
import { ConversationMediaGroups } from "@/features/chat/ConversationMediaGroup";
import { projectAnswerSources } from "@/features/chat/conversationProjection";
import type { ConversationCreativeMediaPart } from "@/features/chat/creativeMediaSchemas";
import type { ConversationExecutionNode } from "@/features/chat/conversationSchemas";
import {
  buildConversationExecutionTree,
  completedOperationSummary,
} from "@/features/chat/executionTree";
import { MerryAnswerSources } from "@/shared/ui/MerryAnswerSources";
import type { ExperienceStrategyProposalAdjustmentReference } from "@/features/experience-strategy/experienceStrategyAdjustment";
import { ExperienceStrategyProposalCard } from "@/features/experience-strategy/ExperienceStrategyProposalCard";
import type { ExperienceStrategyProposalPart } from "@/features/experience-strategy/experienceStrategySchemas";
import { capabilityReadinessActionPath } from "@/features/capability-readiness/capabilityReadinessPresentation";
import { useCapabilityReadinessProbe } from "@/features/capability-readiness/useCapabilityReadinessProbe";
import { MerryButton } from "@/shared/ui/MerryButton";

gsap.registerPlugin(useGSAP);

interface ConversationAssistantResponseProps {
  conversationId: string;
  contentMode?: "standard" | "read-only";
  contentHash: string | null;
  createdAt: string | null;
  creativeMediaParts: ReadonlyArray<ConversationCreativeMediaPart>;
  executionNodes: ReadonlyArray<ConversationExecutionNode>;
  experienceStrategyProposalParts: ReadonlyArray<ExperienceStrategyProposalPart>;
  merchantHomeBindingParts?: ReadonlyArray<MerchantHomeBindingPart>;
  messageId: string;
  isLatestCompleted: boolean;
  message: string;
  onOpenMediaCanvas: (jobId: string, resourceId?: string) => void;
  onAdjustExperienceStrategy: (
    reference: ExperienceStrategyProposalAdjustmentReference,
  ) => void;
  onQuote: (text: string, sourceProjectionText: string) => void;
  organizationId: string;
  sequence: number | null;
  shopBindingId?: string;
}

type ManualDisclosureState = ReadonlyMap<string, boolean>;

export function ConversationAssistantResponse({
  conversationId,
  contentMode = "standard",
  contentHash,
  createdAt,
  creativeMediaParts,
  executionNodes,
  experienceStrategyProposalParts,
  merchantHomeBindingParts = [],
  messageId,
  isLatestCompleted,
  message,
  onOpenMediaCanvas,
  onAdjustExperienceStrategy,
  onQuote,
  organizationId,
  sequence,
  shopBindingId = "",
}: ConversationAssistantResponseProps) {
  const [actionNotice, setActionNotice] = useState("");
  const probe = useCapabilityReadinessProbe(organizationId, shopBindingId);
  const tree = useMemo(
    () => buildConversationExecutionTree(executionNodes),
    [executionNodes],
  );
  const hasActiveNode = tree.activeNodeId !== null;
  const activeNode = tree.activeNodeId
    ? (tree.nodeById.get(tree.activeNodeId) ?? null)
    : null;
  const hasResult = message.trim().length > 0;
  const [rootManualExpanded, setRootManualExpanded] = useState<boolean | null>(
    null,
  );
  const [manualDisclosures, setManualDisclosures] =
    useState<ManualDisclosureState>(() => new Map());
  const rootExpanded = rootManualExpanded ?? hasActiveNode;
  const rootPanelId = `${useId()}-execution`;
  const markdownDocument = useMerryMarkdownDocument({
    cacheKey: contentHash ?? messageId,
    isStreaming: contentHash === null,
    source: message,
  });
  const answerSources = useMemo(
    () => projectAnswerSources(executionNodes),
    [executionNodes],
  );
  const timestamp = formatTimestamp(createdAt);
  const processedSummary = completedOperationSummary(tree.nodeById.values());
  const failedRoot = tree.roots.some((branch) => branch.node.status === "failed");
  const stoppedRoot = tree.roots.some((branch) => branch.node.status === "canceled");
  const copyAnswer = async () => {
    try {
      await navigator.clipboard.writeText(markdownDocument.projection.text);
      setActionNotice("回答已复制");
    } catch { setActionNotice("复制失败，请允许剪贴板权限，或直接选中文字复制。"); }
  };
  const exportAnswer = () => {
    const url = URL.createObjectURL(new Blob([markdownDocument.projection.text], { type: "text/plain;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `Merry-回答-${messageId.replace(/[^a-zA-Z0-9_-]/g, "_")}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    setActionNotice("回答已导出为 TXT");
  };

  const toggleNode = (nodeId: string, currentExpanded: boolean) => {
    setManualDisclosures((current) => {
      const next = new Map(current);
      next.set(nodeId, !currentExpanded);
      return next;
    });
  };

  return (
    <article
      className="conversation-assistant-response"
      data-active={hasActiveNode}
      data-execution-diagnostics={tree.diagnostics.length || undefined}
      data-figma-source-node-ids="612:70924,612:71416,612:72154,612:72400,612:71170,612:71908,612:71662,641:87210,641:88710,641:88349,641:89004"
    >
      <div className="conversation-assistant-response__header">
        <div className="conversation-assistant-response__identity">
          <img alt="" aria-hidden="true" src={chatAiIcon} />
          <strong>Merry</strong>
          {tree.roots.length > 0 ? (
            <Button
              aria-controls={rootPanelId}
              aria-expanded={rootExpanded}
              className="conversation-assistant-response__summary"
              onPress={() => setRootManualExpanded(!rootExpanded)}
            >
              <span>
                · {activeNode
                  ? executionStatusLabel(activeNode.status)
                  : failedRoot
                    ? "回答未完成"
                    : stoppedRoot
                      ? "已停止"
                      : processedSummary}
              </span>
              <img
                alt=""
                aria-hidden="true"
                src={rootExpanded ? arrowDownIcon : arrowRightIcon}
              />
            </Button>
          ) : null}
        </div>
        <MerryAnswerSources
          data-testid="conversation-answer-sources"
          sources={answerSources}
        />
        {isLatestCompleted && !hasActiveNode && !failedRoot && !stoppedRoot && timestamp ? (
          <span className="conversation-assistant-response__status">
            <time>{timestamp}</time>
            <img alt="" aria-hidden="true" src={checkIcon} />
          </span>
        ) : null}
      </div>

      {hasActiveNode ? (
        <p className="conversation-assistant-response__thinking-status" role="status">
          {activeNode?.status === "waiting_input"
            ? "等待补充信息后继续"
            : activeNode?.status === "approval_required"
              ? "等待您确认后继续"
              : "正在帮您找到最佳方案"}
        </p>
      ) : null}

      {tree.roots.length > 0 ? (
        <AnimatedDisclosurePanel
          className="conversation-assistant-response__execution"
          id={rootPanelId}
          isOpen={rootExpanded}
        >
          <MerryExecutionSlot>
            {tree.orderedNodes.map((node) => (
              <MerryExecutionNodeRow
                autoExpanded={tree.activeNodeId === node.nodeId}
                node={node}
                key={node.nodeId}
                manualDisclosures={manualDisclosures}
                onToggle={toggleNode}
                probe={probe}
                shopBindingId={shopBindingId}
              />
            ))}
          </MerryExecutionSlot>
        </AnimatedDisclosurePanel>
      ) : null}

      {hasResult ? (
        <MerryMarkdownDocumentView
          className="conversation-assistant-response__text"
          document={markdownDocument}
          selectable={
            contentHash && sequence
              ? { contentHash, messageId, sequence }
              : undefined
          }
        />
      ) : null}

      {contentMode === "standard" && creativeMediaParts.length > 0 ? (
        <ConversationMediaGroups
          conversationId={conversationId}
          onOpenCanvas={onOpenMediaCanvas}
          organizationId={organizationId}
          parts={creativeMediaParts}
          shopBindingId={shopBindingId}
        />
      ) : null}

      {contentMode === "standard"
        ? merchantHomeBindingParts.map((part) => (
            <ConversationStoreBindingCard
              conversationId={conversationId}
              key={part.data.card_id}
              organizationId={organizationId}
              part={part}
            />
          ))
        : null}

      {contentMode === "standard" && experienceStrategyProposalParts.length > 0 ? (
        <div className="conversation-assistant-response__strategy-proposals">
          {experienceStrategyProposalParts.map((part) => (
            <ExperienceStrategyProposalCard
              immutablePart={part}
              key={part.data.proposal_id}
              onAdjust={onAdjustExperienceStrategy}
              organizationId={organizationId}
              shopBindingId={shopBindingId}
            />
          ))}
        </div>
      ) : null}

      {hasResult ? (
        <div
            aria-label="回答辅助功能"
            className="conversation-assistant-response__actions"
            role="toolbar"
          >
            <Button
              aria-label="复制回答"
              className="conversation-assistant-response__action"
              isDisabled={markdownDocument.pending}
              onPress={() => void copyAnswer()}
            >
              <img alt="" aria-hidden="true" src={copyIcon} />
            </Button>
            <Button
              aria-label="引用回答"
              className="conversation-assistant-response__action"
              isDisabled={markdownDocument.pending}
              onPress={() =>
                onQuote(
                  markdownDocument.projection.text,
                  markdownDocument.projection.text,
                )
              }
            >
              <img alt="" aria-hidden="true" src={quoteIcon} />
            </Button>
            <MenuTrigger>
            <Button
              aria-label="更多操作"
              className="conversation-assistant-response__action"
              isDisabled={markdownDocument.pending}
            >
              <img alt="" aria-hidden="true" src={moreIcon} />
            </Button>
            <Popover placement="top end" className="rounded-3xl border border-white/60 bg-white/90 p-2 shadow-xl backdrop-blur-lg">
              <Menu aria-label="回答更多操作" className="min-w-40 outline-none" onAction={(key) => {
                if (key === "copy") void copyAnswer();
                else if (key === "quote") onQuote(markdownDocument.projection.text, markdownDocument.projection.text);
                else if (key === "export") exportAnswer();
              }}>
                <MenuItem id="copy" className="cursor-pointer rounded-2xl px-3 py-2 text-sm outline-none data-[focused]:bg-blue-50">复制回答</MenuItem>
                <MenuItem id="quote" className="cursor-pointer rounded-2xl px-3 py-2 text-sm outline-none data-[focused]:bg-blue-50">引用回答</MenuItem>
                <MenuItem id="export" className="cursor-pointer rounded-2xl px-3 py-2 text-sm outline-none data-[focused]:bg-blue-50">导出回答 TXT</MenuItem>
              </Menu>
            </Popover>
            </MenuTrigger>
        </div>
      ) : null}
      {actionNotice ? <p role="status" className="px-6 text-xs text-[#19262b]/70">{actionNotice}</p> : null}
    </article>
  );
}

function MerryExecutionNodeRow({
  autoExpanded,
  node,
  manualDisclosures,
  onToggle,
  probe,
  shopBindingId,
}: {
  autoExpanded: boolean;
  node: ConversationExecutionNode;
  manualDisclosures: ManualDisclosureState;
  onToggle: (nodeId: string, currentExpanded: boolean) => void;
  probe: ReturnType<typeof useCapabilityReadinessProbe>;
  shopBindingId: string;
}) {
  const busy = node.status === "queued" || node.status === "running";
  const expandable =
    node.detailBlocks.length > 0 ||
    node.sources.length > 0 ||
    node.summary.trim().length > 0;
  const manualExpanded = manualDisclosures.get(node.nodeId);
  const expanded = manualExpanded ?? autoExpanded;
  const panelId = `${useId()}-details`;
  const rowRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLSpanElement>(null);

  useGSAP(
    () => {
      const row = rowRef.current;
      if (!row) return;
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (reduced) return;
      gsap.fromTo(
        row,
        { autoAlpha: 0, y: 6 },
        {
          autoAlpha: 1,
          duration: 0.32,
          ease: "power2.out",
          overwrite: "auto",
          y: 0,
        },
      );
    },
    { scope: rowRef },
  );

  useGSAP(
    () => {
      const spinner = spinnerRef.current;
      if (!spinner) return;
      gsap.killTweensOf(spinner);
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (busy && !reduced) {
        gsap.to(spinner, {
          duration: 1.1,
          ease: "none",
          repeat: -1,
          rotate: 360,
        });
      } else {
        gsap.set(spinner, { clearProps: "transform" });
      }
      return () => gsap.killTweensOf(spinner);
    },
    { dependencies: [busy], scope: rowRef },
  );

  return (
    <div
      className="conversation-execution-node"
      data-depth={node.depth}
      data-status={node.status}
      ref={rowRef}
    >
      <div className="conversation-execution-node__row">
        <span
          aria-hidden="true"
          className="conversation-execution-node__state"
          ref={spinnerRef}
        >
          <img alt="" src={busy ? loadIcon : executionKindIcon(node.kind)} />
        </span>
        <Button
          aria-controls={expandable ? panelId : undefined}
          aria-expanded={expandable ? expanded : undefined}
          aria-label={`${node.title}，${executionStatusLabel(node.status)}`}
          className="conversation-execution-node__trigger"
          isDisabled={!expandable}
          onPress={() => onToggle(node.nodeId, expanded)}
        >
          <strong>{node.title}</strong>
          {node.durationMs !== null ? (
            <span>· {formatDuration(node.durationMs)}</span>
          ) : null}
          {expandable ? (
            <img
              alt=""
              aria-hidden="true"
              src={expanded ? arrowDownIcon : arrowRightIcon}
            />
          ) : null}
        </Button>
        <span className="conversation-execution-node__hover-meta">
          {node.sources[0]?.label ||
            node.summary ||
            executionStatusLabel(node.status)}
          {node.completedAt ? ` · ${formatTimestamp(node.completedAt)}` : ""}
        </span>
      </div>

      {expandable ? (
        <AnimatedDisclosurePanel
          className="conversation-execution-node__body"
          id={panelId}
          isOpen={expanded}
        >
          <MerryExecutionSlot>
            {node.summary ? <p>{node.summary}</p> : null}
            <ExecutionDetailBlocks
              blocks={node.detailBlocks}
              probe={probe}
              shopBindingId={shopBindingId}
            />
            {node.sources.length > 0 ? (
              <ul className="conversation-execution-node__sources">
                {node.sources.map((source, index) => (
                  <li
                    key={`${source.resourceId ?? source.locator ?? source.label}-${index}`}
                  >
                    {safePublicSourceLink(source) ? (
                      <a
                        href={source.locator}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {source.label}
                      </a>
                    ) : source.label}
                    {source.observedAt ? (
                      <time dateTime={source.observedAt}>
                        {` · ${formatTimestamp(source.observedAt)}`}
                      </time>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </MerryExecutionSlot>
        </AnimatedDisclosurePanel>
      ) : null}
    </div>
  );
}

function AnimatedDisclosurePanel({
  children,
  className,
  id,
  isOpen,
}: {
  children: ReactNode;
  className: string;
  id: string;
  isOpen: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const firstRenderRef = useRef(true);
  const [hasOpened, setHasOpened] = useState(isOpen);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!isOpen || hasOpened) return;
    const frame = window.requestAnimationFrame(() => setHasOpened(true));
    return () => window.cancelAnimationFrame(frame);
  }, [hasOpened, isOpen]);

  useGSAP(
    () => {
      const panel = panelRef.current;
      const content = contentRef.current;
      if (!panel || !content) return;
      if (isOpen && !hasOpened) return;
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      gsap.killTweensOf([panel, content]);

      if (firstRenderRef.current || reduced) {
        firstRenderRef.current = false;
        gsap.set(panel, {
          height: isOpen ? "auto" : 0,
          overflow: isOpen ? "visible" : "hidden",
        });
        gsap.set(content, { autoAlpha: isOpen ? 1 : 0, y: 0 });
        setAnimating(false);
        return;
      }

      setAnimating(true);
      if (isOpen) {
        const startHeight = panel.getBoundingClientRect().height;
        gsap.set(panel, { height: "auto", overflow: "hidden" });
        const targetHeight = panel.scrollHeight;
        gsap.set(panel, { height: startHeight });
        gsap.set(content, { autoAlpha: 0, y: 4 });
        gsap.to(panel, {
          duration: 0.34,
          ease: "power2.out",
          height: targetHeight,
          onComplete: () => {
            gsap.set(panel, { clearProps: "height,overflow" });
            setAnimating(false);
          },
          overwrite: "auto",
        });
        gsap.to(content, {
          autoAlpha: 1,
          delay: 0.06,
          duration: 0.24,
          ease: "power2.out",
          overwrite: "auto",
          y: 0,
        });
        return;
      }

      gsap.set(panel, {
        height: panel.getBoundingClientRect().height,
        overflow: "hidden",
      });
      gsap.to(content, {
        autoAlpha: 0,
        duration: 0.14,
        ease: "power1.in",
        overwrite: "auto",
        y: -2,
      });
      gsap.to(panel, {
        delay: 0.04,
        duration: 0.26,
        ease: "power2.inOut",
        height: 0,
        onComplete: () => setAnimating(false),
        overwrite: "auto",
      });
    },
    { dependencies: [hasOpened, isOpen], scope: panelRef },
  );

  return (
    <div
      aria-hidden={!isOpen}
      className={className}
      data-execution-animating={animating}
      id={id}
      inert={!isOpen}
      ref={panelRef}
    >
      <div ref={contentRef}>{hasOpened ? children : null}</div>
    </div>
  );
}

function MerryExecutionSlot({ children }: { children: ReactNode }) {
  return <div className="conversation-execution-slot">{children}</div>;
}

function ExecutionDetailBlocks({
  blocks,
  probe,
  shopBindingId,
}: {
  blocks: ConversationExecutionNode["detailBlocks"];
  probe: ReturnType<typeof useCapabilityReadinessProbe>;
  shopBindingId: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case "text":
            return <p key={`${block.type}-${index}`}>{block.text}</p>;
          case "markdown":
            return (
              <MerryMarkdown
                cacheKey={`execution:${block.text}`}
                density="compact"
                key={`${block.type}-${index}`}
                source={block.text}
              />
            );
          case "key_value":
            return (
              <dl key={`${block.type}-${index}`}>
                {block.items.map((item) => (
                  <div key={`${item.label}:${item.value}`}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            );
          case "table":
            return (
              <div
                className="conversation-execution-node__table-wrap"
                key={`${block.type}-${index}`}
              >
                <table>
                  <thead>
                    <tr>
                      {block.columns.map((column) => (
                        <th key={column}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "metric":
            return (
              <p key={`${block.type}-${index}`}>
                <strong>{block.label}</strong> {block.value}
                {block.unit ?? ""}
              </p>
            );
          case "progress":
            return (
              <p key={`${block.type}-${index}`}>
                <strong>{block.label}</strong> {block.current}/{block.total}
                {block.unit ?? ""}
              </p>
            );
          case "resource_ref":
            return <p key={`${block.type}-${index}`}>{block.label}</p>;
          case "receipt":
            return (
              <p key={`${block.type}-${index}`}>
                <strong>{block.label}</strong> {block.value ?? block.status}
              </p>
            );
          case "error":
            return (
              <p
                className="conversation-execution-node__error"
                key={`${block.type}-${index}`}
              >
                {block.message}
                {isShopAuthorizationError(block.code) ? (
                  <a href="/settings/stores">查看店铺授权</a>
                ) : isMerchantContextConflictError(block.code) ? (
                  <a href="/workspace">回首页开新会话</a>
                ) : null}
              </p>
            );
          case "capability_readiness":
            return (
              <dl
                className="conversation-execution-node__capability-error"
                key={`${block.type}-${index}`}
              >
                <div><dt>能力</dt><dd>{block.capability_key}</dd></div>
                <div><dt>公开错误</dt><dd>{block.public_error_code}</dd></div>
                <div><dt>可重试</dt><dd>{block.retryable ? "是" : "否"}</dd></div>
                {block.observed_at ? <div><dt>观测时间</dt><dd>{new Date(block.observed_at).toLocaleString("zh-CN")}</dd></div> : null}
                {block.request_id ? <div><dt>请求 ID</dt><dd>{block.request_id}</dd></div> : null}
              </dl>
            );
          case "capability_action": {
            const target = capabilityReadinessActionPath(block.action, {
              returnTo: `${location.pathname}${location.search}${location.hash}`,
              shopBindingId: block.shop_binding_id,
            });
            if (!target && block.action !== "retry_probe") return null;
            return (
              <MerryButton
                hierarchy="level2"
                isDisabled={probe.probing}
                key={`${block.type}-${block.action}-${index}`}
                onPress={() => {
                  if (block.action === "retry_probe") {
                    void probe.retry(block.shop_binding_id || shopBindingId);
                    return;
                  }
                  if (target) navigate(target);
                }}
              >
                {probe.probing && block.action === "retry_probe" ? "正在检查…" : block.label}
              </MerryButton>
            );
          }
        }
      })}
    </>
  );
}

function safePublicSourceLink(source: ConversationExecutionNode["sources"][number]) {
  if (source.kind !== "web" || !source.locator) return false;
  try {
    const url = new URL(source.locator);
    const hostname = url.hostname.toLowerCase();
    return url.protocol === "https:" && Boolean(url.hostname) &&
      !url.username && !url.password &&
      hostname.includes(".") &&
      !hostname.includes(":") &&
      !/^(?:\d{1,3}\.){3}\d{1,3}$/u.test(hostname) &&
      !/(?:^|\.)(?:localhost|local|internal|test|invalid)$/u.test(hostname);
  } catch {
    return false;
  }
}

function isShopAuthorizationError(code: string) {
  return /^cwm_merchant_context_(missing|ambiguous)$/u.test(code);
}

function isMerchantContextConflictError(code: string) {
  return code === "cwm_merchant_context_conflict";
}

function executionKindIcon(kind: ConversationExecutionNode["kind"]) {
  switch (kind) {
    case "reasoning_summary": return thinkIcon;
    case "retrieval": return retrievalIcon;
    case "tool": return toolIcon;
    case "approval": return approvalIcon;
    case "phase":
    case "execution":
    case "result": return chatAiIcon;
  }
}

function executionStatusLabel(status: ConversationExecutionNode["status"]) {
  switch (status) {
    case "queued":
      return "等待处理";
    case "running":
      return "正在处理";
    case "waiting_input":
      return "等待补充信息";
    case "approval_required":
      return "等待确认";
    case "succeeded":
      return "已完成";
    case "failed":
      return "处理失败";
    case "canceled":
      return "已停止";
  }
}

function formatTimestamp(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
  }).format(date);
}

function formatDuration(durationMs: number) {
  if (durationMs < 1_000) return `${durationMs}ms`;
  return `${Math.max(1, Math.round(durationMs / 1_000))}s`;
}
